import React from 'react';
import styled from 'styled-components';
import type { PaginationModel } from '../types/table';
import type { DocumentListActionId } from './DocumentTable';
import { DocumentSummaryHeader, type SummaryAction } from './DocumentSummaryHeader';
import { DocumentTable, type DateRangeValue } from './DocumentTable';
import type { DocumentDate, DocumentOverviewItem, DocumentSummaryStats, TaxonomyTreeNode } from '../types';

interface DocumentOverviewProps {
    summary: {
        title: string;
        subtitle?: string;
        stats: DocumentSummaryStats;
        reviewStatusLabel?: string;
        lastUpdatedAt?: string;
        lastUpdatedBy?: string;
        actions?: SummaryAction[];
        datasetPath?: string;
        onDuplicatesClick?: () => void;
        filevineProjectId?: number | null;
        filevineProjectName?: string | null;
    };
    documents: DocumentOverviewItem[];
    onDocumentAction?: (action: DocumentListActionId, docId: string) => void;
    onDocumentRename?: (docId: string, title: string) => Promise<void> | void;
    disabledDocumentActions?: DocumentListActionId[];
    selectedDocumentId?: string;
    emptyStateMessage?: string;
    thumbnailsByDoc?: Record<string, (string | null)[]>;
    onRequestThumbnails?: (document: DocumentOverviewItem) => void;
    onWarningClick?: (warningId: string, docId: string) => void;
    tagPool?: string[];
    onTagsChange?: (docId: string, tags: string[]) => void | Promise<void>;
    onCreateTag?: (tag: string) => void | Promise<void>;
    onDocumentDateChange?: (docId: string, date: DocumentDate | null) => void | Promise<void>;
    paginationModel: PaginationModel;
    onPaginationModelChange: (model: PaginationModel) => void;
    rowCount: number;
    isLoading?: boolean;
    searchQuery: string;
    onSearchQueryChange: (value: string) => void;
    sortOption: string;
    onSortOptionChange: (value: string) => void;
    activeStatus?: string | null;
    onStatusFilterChange?: (status: string | null) => void;
    activeCategory?: string | null;
    onCategoryFilterChange?: (value: string | null) => void;
    categoryTree?: TaxonomyTreeNode[];
    dateRange?: DateRangeValue | null;
    onDateRangeChange?: (range: DateRangeValue | null) => void;
    duplicateFilterDocId?: string | null;
    onDuplicateFilterChange?: (docId: string | null) => void;
    selectedDocIds?: string[];
    onToggleSelection?: (
        docId: string,
        opts: { range?: boolean; visibleDocIds: string[] },
    ) => void;
    onToggleSelectAllOnPage?: (visibleDocIds: string[], select: boolean) => void;
    onClearSelection?: () => void;
    onMergeSelected?: () => void;
    isMergingDocs?: boolean;
}

const Layout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

// Contextual action bar shown when one or more documents are selected.
// Sits above the table so the user's eye stays in roughly the same place
// once they've made a selection. The bar is the only spot bulk actions live
// — we deliberately do not surface merge as a per-row affordance, because
// the analyst's mental model is "these N belong together" and per-row
// merge forces an N-1 round-trip slog (see UX rationale notes in chat).
const SelectionBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 16px;
    background: #fafafa;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
    color: #333;
`;

const SelectionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const SelectionCount = styled.span`
    font-weight: 600;
`;

const ClearLink = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    color: #666;
    text-decoration: underline;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;

    &:hover { color: #111; }
`;

const SelectionActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MergeSelectedButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 6px;
    border: 1px solid #111;
    background: #111;
    color: #fff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    transition: opacity 0.15s;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        opacity: 0.85;
    }
`;

export const DocumentOverview: React.FC<DocumentOverviewProps> = ({
    summary,
    documents,
    onDocumentAction,
    onDocumentRename,
    disabledDocumentActions,
    selectedDocumentId,
    emptyStateMessage = 'No documents match the current filters.',
    thumbnailsByDoc,
    onRequestThumbnails,
    onWarningClick,
    tagPool,
    onTagsChange,
    onCreateTag,
    onDocumentDateChange,
    paginationModel,
    onPaginationModelChange,
    rowCount,
    isLoading = false,
    searchQuery,
    onSearchQueryChange,
    sortOption,
    onSortOptionChange,
    activeStatus = null,
    onStatusFilterChange,
    activeCategory = null,
    onCategoryFilterChange,
    categoryTree,
    dateRange,
    onDateRangeChange,
    duplicateFilterDocId,
    onDuplicateFilterChange,
    selectedDocIds,
    onToggleSelection,
    onToggleSelectAllOnPage,
    onClearSelection,
    onMergeSelected,
    isMergingDocs = false,
}) => {
    const selectionCount = selectedDocIds?.length ?? 0;
    const showSelectionBar = selectionCount > 0;
    const canMerge = selectionCount >= 2 && !isMergingDocs;
    const mergeTooltip = selectionCount < 2
        ? 'Select at least 2 documents to merge'
        : isMergingDocs
            ? 'Merge in progress…'
            : `Merge ${selectionCount} documents into one`;

    return (
        <Layout>
            <DocumentSummaryHeader
                title={summary.title}
                subtitle={summary.subtitle}
                summary={summary.stats}
                reviewStatusLabel={summary.reviewStatusLabel}
                lastUpdatedAt={summary.lastUpdatedAt}
                lastUpdatedBy={summary.lastUpdatedBy}
                actions={summary.actions}
                datasetPath={summary.datasetPath}
                onDuplicatesClick={summary.onDuplicatesClick}
                filevineProjectId={summary.filevineProjectId}
                filevineProjectName={summary.filevineProjectName}
            />

            {showSelectionBar && (
                <SelectionBar role="region" aria-label="Bulk actions">
                    <SelectionInfo>
                        <SelectionCount>
                            {selectionCount} document{selectionCount === 1 ? '' : 's'} selected
                        </SelectionCount>
                        {onClearSelection && (
                            <ClearLink type="button" onClick={onClearSelection}>
                                Clear selection
                            </ClearLink>
                        )}
                    </SelectionInfo>
                    <SelectionActions>
                        <MergeSelectedButton
                            type="button"
                            disabled={!canMerge || !onMergeSelected}
                            title={mergeTooltip}
                            aria-label={mergeTooltip}
                            onClick={onMergeSelected}
                        >
                            {isMergingDocs ? 'Merging…' : 'Merge selected'}
                        </MergeSelectedButton>
                    </SelectionActions>
                </SelectionBar>
            )}

            <DocumentTable
                documents={documents}
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                searchQuery={searchQuery}
                onSearchQueryChange={onSearchQueryChange}
                sortOption={sortOption}
                onSortOptionChange={onSortOptionChange}
                activeStatus={activeStatus}
                onStatusFilterChange={onStatusFilterChange}
                activeCategory={activeCategory}
                onCategoryFilterChange={onCategoryFilterChange}
                categoryTree={categoryTree}
                dateRange={dateRange}
                onDateRangeChange={onDateRangeChange}
                selectedDocumentId={selectedDocumentId}
                onDocumentAction={onDocumentAction}
                onDocumentRename={onDocumentRename}
                disabledDocumentActions={disabledDocumentActions}
                thumbnailsByDoc={thumbnailsByDoc}
                onRequestThumbnails={onRequestThumbnails}
                emptyStateMessage={emptyStateMessage}
                isLoading={isLoading}
                onWarningClick={onWarningClick}
                tagPool={tagPool}
                onTagsChange={onTagsChange}
                onCreateTag={onCreateTag}
                onDocumentDateChange={onDocumentDateChange}
                duplicateFilterDocId={duplicateFilterDocId}
                onDuplicateFilterChange={onDuplicateFilterChange}
                selectedDocIds={selectedDocIds}
                onToggleSelection={onToggleSelection}
                onToggleSelectAllOnPage={onToggleSelectAllOnPage}
            />
        </Layout>
    );
};
